/* ============================================================
   By Sophi · Direction D — cart drawer behaviors.

   Responsibilities:
     - Free-shipping meter: keep math live across cart changes.
     - 2+1 promo step strip: reflect piece count + applied state.
     - "Last chance to add" cross-sell: add-to-cart via /cart/add.js.
     - Mirror cart subtotal into the checkout button label.

   Reads cart state from JSON returned by /cart.js after each change,
   plus listens for theme.CartForm's 'cart:updated' event.
   ============================================================ */
(function () {
  'use strict';

  var DRAWER_SEL = '#CartDrawer';
  var CART_FETCH_OPTS = { credentials: 'same-origin', headers: { 'Accept': 'application/json' } };

  /* EOFY window override (2026-06). Bundle tier mechanic is paused; a single
     Automatic 20% sitewide discount applies for every customer with any cart
     items. When this is true, syncFromCart sets the promo title to either
     a discount-applied message (when a discount is allocated) or the static EOFY
     headline (otherwise). The tier hint and progress dots are suppressed.
     Jul 1 revert: flip EOFY_MODE to false. The else branch below restores the
     original 5/10/15% tier logic byte-for-byte. */
  var EOFY_MODE = false;

  /* Christmas in July window (2026-07). When true, suppress all tier promo bar
     manipulation in syncFromCart (it's hidden in Liquid). The discount-input
     expander handles WELCOME20 application. Jul-end revert: flip CIJ_MODE to false. */
  var CIJ_MODE = true;

  function fmt(amountCents) {
    var n = (amountCents / 100);
    if (Math.abs(n - Math.round(n)) < 0.005) return '$' + Math.round(n);
    return '$' + n.toFixed(2);
  }

  function getDrawer() { return document.querySelector(DRAWER_SEL); }

  /* Helper: update the codebox <details> summary to reflect applied/default state.
     Used by both bindCodebox (after a successful apply) and syncFromCart (on drawer open/sync). */
  function updateCodeboxSummary(drawer, promoSaved, origTotal) {
    var codebox = drawer.querySelector('[data-cdd-codebox]');
    if (!codebox) return;
    var summary = codebox.querySelector('summary');
    if (!summary) return;

    var CHEVRON_SVG = '<svg class="cdd__codebox-chevron" viewBox="0 0 12 8" aria-hidden="true"><path d="M1 1l5 5 5-5" stroke="currentColor" stroke-width="1.6" fill="none" stroke-linecap="round"/></svg>';

    if (promoSaved > 0) {
      codebox.classList.add('is-on');
      var pct = (origTotal && origTotal > 0) ? Math.floor((promoSaved * 100) / origTotal) : 0;
      var savedStr = fmt(promoSaved);
      summary.innerHTML =
        '<span class="cdd__codebox-summary cdd__codebox-summary--applied">' +
          '<span class="cdd__codebox-title">&#10003; ' + (pct > 0 ? pct + '% off applied' : 'Discount applied') + '</span>' +
          '<span class="cdd__codebox-saved">saved ' + savedStr + '</span>' +
          CHEVRON_SVG +
        '</span>';
    } else {
      codebox.classList.remove('is-on');
      summary.innerHTML =
        '<span class="cdd__codebox-summary">' +
          '<span>Have a discount code?</span>' +
          CHEVRON_SVG +
        '</span>';
    }
  }

  function syncFromCart(cart) {
    var drawer = getDrawer();
    if (!drawer) return;

    drawer.setAttribute('data-cdd-subtotal', cart.total_price);
    drawer.setAttribute('data-cdd-pieces', cart.item_count);

    // Header piece count
    var countEl = drawer.querySelector('[data-cdd-count]');
    if (countEl) {
      var c = cart.item_count;
      countEl.textContent = c === 0 ? 'empty' : (c === 1 ? '1 piece' : c + ' pieces');
    }

    var threshold = parseInt(drawer.getAttribute('data-cdd-threshold'), 10) || 7500;
    var subtotal = cart.total_price;
    var remaining = Math.max(0, threshold - subtotal);
    var progress = threshold > 0 ? Math.min(100, Math.round((subtotal / threshold) * 100)) : 0;

    // Meter text
    var meterText = drawer.querySelector('[data-cdd-meter-text]');
    if (meterText) {
      meterText.innerHTML = remaining > 0
        ? 'Add <strong>' + fmt(remaining) + '</strong> for free shipping'
        : '<span class="cdd__check">&#10003;</span> Free shipping unlocked';
    }
    var fill = drawer.querySelector('[data-cdd-meter-fill]');
    if (fill) fill.style.width = progress + '%';

    // Graduated promo — detect savings via multiple fields, fall back to total-price delta.
    // Shopify's automatic "Buy X get Y" discounts can appear in different fields depending on
    // discount type, cart state, and timing — accumulate across all known fields, then use
    // (original_total_price - total_price) as a final fallback.
    var promoSaved = 0;
    (cart.cart_level_discount_applications || []).forEach(function (d) {
      promoSaved += d.total_allocated_amount || 0;
    });
    (cart.items || []).forEach(function (it) {
      (it.line_level_discount_allocations || it.discount_allocations || []).forEach(function (a) {
        promoSaved += a.amount || 0;
      });
      if (!(it.line_level_discount_allocations || it.discount_allocations) && it.total_discount) {
        promoSaved += it.total_discount;
      }
    });
    // Fallback: if no allocation fields are populated yet, use the price delta.
    if (promoSaved === 0 && cart.original_total_price && cart.total_price && cart.original_total_price > cart.total_price) {
      promoSaved = cart.original_total_price - cart.total_price;
    }

    var promoTierReached = EOFY_MODE ? (cart.item_count >= 1) : (cart.item_count >= 2);
    var promoActive = EOFY_MODE ? (promoSaved > 0) : (promoTierReached || promoSaved > 0);
    drawer.setAttribute('data-cdd-promo-active', promoActive ? 'true' : 'false');
    drawer.setAttribute('data-cdd-promo-amount', promoSaved);

    var promo = drawer.querySelector('[data-cdd-promo]');
    if (promo) {
      if (CIJ_MODE) {
        // C-in-J: tier promo bar is hidden in Liquid; nothing to sync.
        // Discount-input expander handles the WELCOME20 path.
        // Strip any stale savings row that a pre-CIJ cached render might have emitted.
        var staleSavingsCIJ = drawer.querySelector('[data-cdd-savings-row]');
        if (staleSavingsCIJ) staleSavingsCIJ.remove();
        // Fall through to checkout total mirror and empty toggle below.
      } else {
        // Tier promo bar is visible — sync it.
        promo.classList.remove('cdd__promo--hidden');
        promo.removeAttribute('hidden');
        promo.classList.toggle('is-on', promoActive);

        if (EOFY_MODE) {
          // EOFY window — title reflects the single sitewide 20% offer. No tier math.
          var title = promo.querySelector('[data-cdd-promo-title]');
          if (title) {
            if (promoSaved > 0 && cart.original_total_price > 0) {
              var displayedPct = Math.floor((promoSaved * 100) / cart.original_total_price);
              title.textContent = displayedPct + '% off applied';
            } else {
              title.textContent = 'EOFY Sale: 20% off everything in June';
            }
          }

          var saved = promo.querySelector('.cdd__promo-saved');
          if (promoSaved > 0) {
            if (!saved) {
              saved = document.createElement('span');
              saved.className = 'cdd__promo-saved';
              promo.querySelector('.cdd__promo-top').appendChild(saved);
            }
            saved.textContent = 'saved ' + fmt(promoSaved);
          } else if (saved) {
            saved.remove();
          }

          var staleTrack = promo.querySelector('[data-cdd-promo-track]');
          if (staleTrack) staleTrack.remove();
          var staleHint = promo.querySelector('[data-cdd-promo-hint]');
          if (staleHint) staleHint.remove();
        } else {
          // Tier mode (post-CIJ). Restored from 2026-05 logic.
          var currentPct = 0, nextPct = 5, nextAt = 2, maxReached = false;
          if (cart.item_count >= 4) { currentPct = 15; maxReached = true; }
          else if (cart.item_count === 3) { currentPct = 10; nextPct = 15; nextAt = 4; }
          else if (cart.item_count === 2) { currentPct = 5; nextPct = 10; nextAt = 3; }

          var titleT = promo.querySelector('[data-cdd-promo-title]');
          if (titleT) {
            if (maxReached) titleT.textContent = currentPct + '% off applied';
            else if (currentPct > 0) titleT.textContent = currentPct + '% off applied';
            else titleT.textContent = 'Bundle & save up to 15%';
          }

          var savedT = promo.querySelector('.cdd__promo-saved');
          if (promoSaved > 0) {
            if (!savedT) {
              savedT = document.createElement('span');
              savedT.className = 'cdd__promo-saved';
              promo.querySelector('.cdd__promo-top').appendChild(savedT);
            }
            savedT.textContent = 'saved ' + fmt(promoSaved);
          } else if (savedT) {
            savedT.remove();
          }

          var track = promo.querySelector('[data-cdd-promo-track]');
          var pieces = cart.item_count;
          var fillRatio = 0;
          if (pieces === 1) fillRatio = 0.125;
          else if (pieces === 2) fillRatio = 0.25;
          else if (pieces === 3) fillRatio = 0.60;
          else if (pieces >= 4) fillRatio = 0.95;
          if (track) track.style.setProperty('--fill', fillRatio);

          var TAG_SVG = '<svg viewBox="0 0 20 20" width="13" height="13" aria-hidden="true" fill="currentColor">' +
            '<path d="M10.59 2H4a2 2 0 0 0-2 2v6.59a2 2 0 0 0 .59 1.41l7 7a2 2 0 0 0 2.83 0l5.59-5.59a2 2 0 0 0 0-2.83l-7-7A2 2 0 0 0 10.59 2zM6.5 7A1.5 1.5 0 1 1 8 5.5 1.5 1.5 0 0 1 6.5 7z"/>' +
            '</svg>';
          var dotThresholds = [2, 3, 4];
          promo.querySelectorAll('.cdd__promo-step').forEach(function (step, i) {
            var dotThreshold = dotThresholds[i];
            var on = pieces >= dotThreshold;
            step.classList.toggle('is-on', on);
            step.classList.add('cdd__promo-step--reward');
            var dot = step.querySelector('.cdd__promo-step-dot');
            if (!dot) return;
            dot.innerHTML = TAG_SVG;
          });

          var hint = promo.querySelector('[data-cdd-promo-hint]');
          if (maxReached) {
            if (hint) hint.remove();
          } else {
            if (!hint) {
              hint = document.createElement('div');
              hint.className = 'cdd__promo-hint';
              hint.setAttribute('data-cdd-promo-hint', '');
              var hintParent = promo.querySelector('.cdd__promo-body') || promo;
              hintParent.appendChild(hint);
            }
            var toUnlock = Math.max(0, nextAt - pieces);
            hint.innerHTML = toUnlock <= 1
              ? 'Add 1 more for <strong>' + nextPct + '% off</strong>'
              : 'Add ' + toUnlock + ' more for <strong>' + nextPct + '% off</strong>';
          }
        }
      }
    }

    // CIJ: sync codebox summary to reflect discount state on drawer open/cart sync
    if (CIJ_MODE) {
      updateCodeboxSummary(drawer, promoSaved, cart.original_total_price);
    }

    // Checkout button total mirror
    var btnTotal = drawer.querySelector('[data-cdd-checkout-total]');
    if (btnTotal) btnTotal.textContent = fmt(cart.total_price);

    // Strip any leftover stale savings rows
    var staleSavings = drawer.querySelector('[data-cdd-savings-row]');
    if (staleSavings) staleSavings.remove();

    // Strip any leftover afterpay node
    var staleAp = drawer.querySelector('[data-cdd-afterpay]');
    if (staleAp) staleAp.remove();

    // Subtotal strike-through on promo
    var subtotalRow = drawer.querySelector('.cdd__totals-prices');
    if (subtotalRow) {
      var was = subtotalRow.querySelector('[data-cdd-subtotal-was]');
      if (promoActive && cart.original_total_price && cart.original_total_price > cart.total_price) {
        if (!was) {
          was = document.createElement('span');
          was.className = 'cdd__totals-was';
          was.setAttribute('data-cdd-subtotal-was', '');
          subtotalRow.insertBefore(was, subtotalRow.firstChild);
        }
        was.textContent = fmt(cart.original_total_price);
      } else if (was) {
        was.remove();
      }
    }

    // Empty toggle (theme.CartForm also does this; harmless duplicate)
    drawer.classList.toggle('is-empty', cart.item_count === 0);
  }

  function refreshFromShopify() {
    return fetch('/cart.js', CART_FETCH_OPTS)
      .then(function (r) { return r.json(); })
      .then(syncFromCart)
      .catch(function () { /* ignore */ });
  }

  /* --- CIJ discount code apply + signup handlers --- */
  function bindCodebox() {
    var drawer = getDrawer();
    if (!drawer) return;
    if (!CIJ_MODE) return;

    drawer.addEventListener('click', function (e) {

      // Apply button
      var applyBtn = e.target.closest('[data-cdd-code-apply]');
      if (applyBtn) {
        e.preventDefault();
        if (applyBtn.getAttribute('aria-busy') === 'true') return;

        var input = drawer.querySelector('[data-cdd-code-input]');
        var feedback = drawer.querySelector('[data-cdd-code-feedback]');
        var codeValue = (input ? input.value : '').trim().toUpperCase();

        if (!codeValue) {
          if (feedback) { feedback.textContent = 'Please enter a code.'; feedback.className = 'cdd__codebox-feedback is-error'; }
          return;
        }

        applyBtn.setAttribute('aria-busy', 'true');
        var origText = applyBtn.textContent;
        applyBtn.textContent = '...';
        if (feedback) { feedback.textContent = ''; feedback.className = 'cdd__codebox-feedback'; }

        // POST discount code to cart via /cart/update.js
        fetch('/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ discount: codeValue })
        })
          .then(function (r) {
            if (!r.ok) throw new Error('update failed');
            return r.json();
          })
          .then(function () {
            // Re-fetch cart to confirm discount was actually applied
            return fetch('/cart.js', CART_FETCH_OPTS).then(function (r) { return r.json(); });
          })
          .then(function (cart) {
            // Check if any discount was applied
            var savedAmount = 0;
            (cart.cart_level_discount_applications || []).forEach(function (d) {
              savedAmount += d.total_allocated_amount || 0;
            });
            (cart.items || []).forEach(function (it) {
              (it.line_level_discount_allocations || it.discount_allocations || []).forEach(function (a) {
                savedAmount += a.amount || 0;
              });
            });
            if (savedAmount === 0 && cart.original_total_price > cart.total_price) {
              savedAmount = cart.original_total_price - cart.total_price;
            }

            if (savedAmount > 0) {
              if (feedback) {
                feedback.textContent = '';
                feedback.className = 'cdd__codebox-feedback';
                // Build success message: checkmark + code + saved amount
                var savedFmt = '$' + (savedAmount / 100).toFixed(2).replace(/\.00$/, '');
                feedback.innerHTML = '&#10003; ' + codeValue + ' applied &middot; saved ' + savedFmt;
              }
              // Update codebox summary to applied state
              updateCodeboxSummary(drawer, savedAmount, cart.original_total_price);
              // Rebuild drawer item list and re-sync state
              document.dispatchEvent(new CustomEvent('cart:build'));
              syncFromCart(cart);
              // Re-dispatch cart:updated so wallet incubator re-reads totals
              document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
              // Attempt wallet element refresh after cart rebuild settles.
              setTimeout(function () {
                var walletSlot = drawer.querySelector('[data-cdd-wallet-slot]');
                if (walletSlot) {
                  var wc = walletSlot.querySelector('shopify-accelerated-checkout-cart');
                  if (wc) {
                    wc.dispatchEvent(new CustomEvent('refresh', { bubbles: false }));
                  }
                }
              }, 400);
            } else {
              if (feedback) {
                feedback.textContent = 'Code not valid or not applicable to this cart.';
                feedback.className = 'cdd__codebox-feedback is-error';
              }
            }
            applyBtn.textContent = origText;
            applyBtn.setAttribute('aria-busy', 'false');
          })
          .catch(function () {
            if (feedback) {
              feedback.textContent = 'Could not apply code. Please try again.';
              feedback.className = 'cdd__codebox-feedback is-error';
            }
            applyBtn.textContent = origText;
            applyBtn.setAttribute('aria-busy', 'false');
          });
        return;
      }

      // Enter key support on input field
    });

    // Enter key on the code input
    drawer.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') {
        var input = e.target.closest('[data-cdd-code-input]');
        if (input) {
          e.preventDefault();
          var applyBtn = drawer.querySelector('[data-cdd-code-apply]');
          if (applyBtn) applyBtn.click();
        }
      }
    });

    // Signup CTA — open Klaviyo welcome form (TtHkta confirmed via board klaviyo-resp-cij-welcome-code-direction-2026-07-01)
    drawer.addEventListener('click', function (e) {
      var signup = e.target.closest('[data-cdd-signup]');
      if (!signup) return;
      e.preventDefault();
      window._klOnsite = window._klOnsite || [];
      window._klOnsite.push(['openForm', 'TtHkta']);
    });
  }

  /* --- Cross-sell add-to-cart --- */
  function bindCrossSell() {
    var drawer = getDrawer();
    if (!drawer) return;

    drawer.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-cdd-add]');
      if (!btn) return;
      e.preventDefault();
      if (btn.getAttribute('aria-busy') === 'true') return;

      var variantId = btn.getAttribute('data-cdd-add');
      if (!variantId) return;
      btn.setAttribute('aria-busy', 'true');
      var originalText = btn.textContent;
      btn.textContent = '...';

      fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      })
        .then(function (r) {
          if (!r.ok) throw new Error('add failed');
          return r.json();
        })
        .then(function () {
          // Trigger Impulse's cart rebuild (rebuilds [data-products] + meter)
          document.dispatchEvent(new CustomEvent('cart:build'));
          return refreshFromShopify();
        })
        .then(function () {
          btn.textContent = '&#10003;';
          setTimeout(function () {
            btn.textContent = originalText;
            btn.setAttribute('aria-busy', 'false');
          }, 800);
        })
        .catch(function () {
          btn.textContent = originalText;
          btn.setAttribute('aria-busy', 'false');
        });
    });
  }

  function bindRemove() {
    /* v1.213/v1.214 — The x delete button is a plain <a href="/cart/change?id=...">.
       Without interception, clicking it navigates to /cart, breaking the drawer
       UX (we land on the cart page instead of staying put with the drawer open).
       Delegate clicks here, call the AJAX cart API, and rebuild in place. */
    document.addEventListener('click', function (evt) {
      var a = evt.target.closest && evt.target.closest('.cdd-row__x');
      if (!a) return;
      evt.preventDefault();
      if (a.getAttribute('aria-busy') === 'true') return;
      a.setAttribute('aria-busy', 'true');

      var row = a.closest('[data-key]');
      var key = row && row.getAttribute('data-key');
      if (!key) return;

      a.style.opacity = '0.4';

      if (window.theme && theme.cart && typeof theme.cart.changeItem === 'function') {
        theme.cart.changeItem(key, 0)
          .then(function () {
            document.dispatchEvent(new CustomEvent('cart:build'));
            return refreshFromShopify();
          })
          .catch(function () {
            a.style.opacity = '';
            a.setAttribute('aria-busy', 'false');
          });
      } else {
        fetch('/cart/change.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: key, quantity: 0 })
        })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            document.dispatchEvent(new CustomEvent('cart:build'));
            syncFromCart(cart);
          })
          .catch(function () {
            a.style.opacity = '';
            a.setAttribute('aria-busy', 'false');
          });
      }
    }, true);
  }

  function init() {
    if (!getDrawer()) return;
    bindCodebox();
    bindCrossSell();
    bindRemove();

    // Sync on every cart change broadcast by theme.CartForm.
    document.addEventListener('cart:updated', function (e) {
      if (e.detail && e.detail.cart) syncFromCart(e.detail.cart);
      else refreshFromShopify();
    });

    // theme.CartForm only dispatches 'cart:updated' on quantity change.
    // When a PDP add-to-cart opens the drawer, no event fires — refresh on open.
    document.addEventListener('drawerOpen.CartDrawer', refreshFromShopify);
    document.addEventListener('drawerOpen', refreshFromShopify);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
